/**
 * Auth client for the dashboard.
 *
 * Uses session cookies (HTTP-only) set by the backend's magic-link callback.
 * All requests use credentials: 'include' so cookies travel.
 *
 * In dev the Vite proxy makes /v1/* same-origin, so cookies just work.
 * In prod the dashboard ships on app.wholisphere.ai and the backend on
 * api.wholisphere.ai — both are wholisphere.ai sub-domains so cookies
 * with `Domain=.wholisphere.ai` work cross-subdomain.
 */

export interface AuthOrg {
  id: string;
  name: string;
  slug: string;
  plan: 'free' | 'pro' | 'platform';
  status: 'active' | 'suspended' | 'canceled';
  region: 'us' | 'eu' | 'apac';
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string;
}

export interface AuthState {
  user: AuthUser;
  orgs: AuthOrg[];
}

import { apiBase } from './api-base.js';

const fetchJson = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const resp = await fetch(`${apiBase()}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const text = await resp.text();
  const json = text ? JSON.parse(text) : null;
  if (!resp.ok) {
    const code =
      (json as { error?: { code?: string } } | null)?.error?.code ?? `http_${resp.status}`;
    const message =
      (json as { error?: { message?: string } } | null)?.error?.message ?? `HTTP ${resp.status}`;
    const e = new Error(message) as Error & { code: string; status: number };
    e.code = code;
    e.status = resp.status;
    throw e;
  }
  return json as T;
};

export const requestMagicLink = async (
  email: string,
  returnTo?: string,
): Promise<{ ok: boolean; devLink?: string; message: string }> => {
  const body: { email: string; returnTo?: string } = { email };
  if (returnTo) body.returnTo = returnTo;
  return fetchJson('/v1/auth/request', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const submitAccessRequest = async (input: {
  email: string;
  name?: string;
  company?: string;
  useCase?: string;
}): Promise<{ ok: boolean; message: string }> => {
  return fetchJson('/v1/access-requests', {
    method: 'POST',
    body: JSON.stringify(input),
  });
};

export const fetchMe = async (): Promise<AuthState> => {
  return fetchJson<AuthState>('/v1/auth/me');
};

export const logout = async (): Promise<void> => {
  await fetchJson('/v1/auth/logout', { method: 'POST' });
};
